import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TableCell,
  TableRow,
  CircularProgress,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Plus,
  X,
  ChevronDown,
  GripVertical,
  Edit,
  Users,
  MoreVertical,
  FileText,
  Calendar,
  Send,
  Trash2,
  Clock,
  CheckCircle,
  Layers,
} from "lucide-react";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { Card, CardContent } from "../../components/Card";
import { Button } from "../../components/Button";
import { CustomSelector } from "../../components/CustomSelector";
import { CustomTextField } from "../../components/CustomTextField";
import { Badge } from "../../components/Badge";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { isClientReadOnly } from "../../utils/clientAccess";
import { DateTimePickerField } from "../../components/DateTimePickerField";
import { DataTable } from "../../components/DataTable";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { PLATFORM_META } from "./workflow/platformMeta";
import { Label } from "../../components/Label";
import { AgencyIcon } from "../../components/icons/AgencyIcon";
import { Avatar } from "../../components/Avatar";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { ContentCalendar } from "./ContentCalendar";
import { UnderlineTabs } from "../Clients/metrics/UnderlineTabs";
import { UpcomingPublications } from "./UpcomingPublications";
import { AgencyPromptsButton } from "../../components/prompts/AgencyPromptsButton";
import { PROMPT_STEPS } from "../../components/prompts/agencyPrompts";
import { getData } from "../../components/hooks/getData";
import { useDelayedLoading } from "../../components/hooks/useDelayedLoading";
import { useAuth } from "../../components/contexts/AuthContext";
import apiClient from "../../api-client";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import SuccessPopup from "../../components/SuccessPopup";
import { getGroupDisplayStatus, getPieceDisplayStatus } from "./workflow/workflowUtils";
import {
  publishingErrorText,
  updatePieceScheduledAt,
} from "../../services/socialPublishingService";
import { ContentConfirmationModal } from "./Modals/ContentConfirmationModal";
import { CreateContentModal, EditBuyerPersonasModal } from "./Modals/CreateContentModal";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import { mountDragPreview } from "../../utils/drag_drop/dragDropUtils";
import { useDragAutoScroll } from "../../utils/drag_drop/useDragAutoScroll";
import { UnreadThreadBadge } from "../../components/UnreadThreadBadge";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import {
  CONTENT_GENERATOR_TUTORIAL_INTRO,
  useTutorialsEnabled,
  TutorialIntroModal,
  TutorialStartButton,
} from "../../Tutorials/index.js";
import { useContentTutorial } from "./ContentTutorialContext";
import {
  CONTENT_TUTORIAL_MOCK_CLIENT,
  CONTENT_TUTORIAL_MOCK_CONFIG_ID,
} from "../../Tutorials/contentGenerator/contentTutorialMocks.js";

// === STATUS CONFIG ===




// === HELPER FUNCTIONS ===

const formatDateTimeInputValue = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const isGroupEntry = (item) => item?.type === "group";

// Earliest schedule among the group's pieces.
const getGroupScheduledAt = (group) =>
  (group.pieces || [])
    .map((piece) => piece.scheduled_at)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0] ?? null;

function DateCell({ value, icon: Icon = Clock }) {
  const theme = useTheme();
  const date = value ? new Date(value) : null;
  return (
    <TableCell>
      <Tooltip
        title={date ? date.toLocaleDateString("es-ES", {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }) : "Sin fecha"}
        arrow
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <Icon size={12} color={theme.palette.text.secondary} />
          <Typography
            variant="caption"
            color="text.primary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: { xs: "0.7rem", md: "0.75rem" },
            }}
          >
            {date
              ? date.toLocaleDateString("es-ES", {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })
              : "-"}
          </Typography>
        </Box>
      </Tooltip>
    </TableCell>
  );
}
const CONTENT_TUTORIAL_PREFERENCE_KEY = "content_generator";


// === COMPONENTS ===

export function ContentGenerator() {
  const theme = useTheme();
  const tutorialsEnabled = useTutorialsEnabled();
  const { neutrals, colors } = useCustomTheme();
  const {
    setPageContext,
    contentGeneratorRefreshKey,
    selectedFilterData,
    filterRefresh,
    changeSelectedFilter,
    updateUiPreferences,
  } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, interceptorReady } = useAuth();

  // Check user role
  const isSuperAdmin = user?.role === "superadmin";
  const isAgencyAdmin = user?.role === "agency_admin";
  const isAgencyScopedUser =
    user?.role === "agency_admin" || user?.role === "consultant";
  const selectedClientFilterId =
    isAgencyScopedUser &&
      selectedFilterData?.id &&
      selectedFilterData.id !== "global"
      ? selectedFilterData.id
      : null;
  const selectedClientFilterName =
    isAgencyScopedUser && selectedClientFilterId
      ? selectedFilterData?.name
      : null;
  const selectedClientFilterConfigId =
    isAgencyScopedUser && selectedClientFilterId
      ? selectedFilterData?.config_id ?? null
      : null;
  const canManageContent = ["consultant", "agency_admin", "superadmin"].includes(
    user?.role
  );

  // State
  const [selectedTab, setSelectedTab] = useState("all"); // "all" or config id
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0); // Total count from backend
  const [statusStats, setStatusStats] = useState({
    draft: 0,
    client_review: 0,
    client_approved: 0,
    scheduled: 0,
    published: 0,
  });

  // Check if user is a client (clients don't see draft content)
  const isClientUser = user?.role === "client";
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isGroupingMode, setIsGroupingMode] = useState(false);
  const [selectedGroupPieceIds, setSelectedGroupPieceIds] = useState([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  // The view lives in the URL (?vista=calendario) so the browser's back button returns to the table.
  const viewMode = searchParams.get("vista") === "calendario" ? "calendar" : "table";
  const setViewMode = useCallback(
    (mode, { replace = false } = {}) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (mode === "calendar") next.set("vista", "calendario");
          else next.delete("vista");
          return next;
        },
        { replace }
      ),
    [setSearchParams]
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [showApproveContentModal, setShowApproveContentModal] = useState(false);
  const [showTutorialIntroModal, setShowTutorialIntroModal] = useState(false);
  const [isFirstTimeTutorialPrompt, setIsFirstTimeTutorialPrompt] = useState(false);
  const [pendingTutorialAction, setPendingTutorialAction] = useState(null);
  const contentTutorialAutoPromptedRef = useRef(false);
  const [pendingGroupMembershipAction, setPendingGroupMembershipAction] =
    useState(null);
  const { isTutorialActive, tutorialMock, startTutorial } = useContentTutorial();
  // Tutorial: the list shows a mock client's contents instead of the real ones.
  const tutorialList = tutorialMock?.list ?? null;
  const [showCreateContentModal, setShowCreateContentModal] = useState(false);
  const [personasEditTarget, setPersonasEditTarget] = useState(null); // { configId, clientName }
  const [isClientFilterModalOpen, setIsClientFilterModalOpen] = useState(false);
  const [draftClientFilters, setDraftClientFilters] = useState({ client: null });
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupError, setIsPopupError] = useState(false);
  const [draggedPieceId, setDraggedPieceId] = useState(null);
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const [isOverUngroupDropZone, setIsOverUngroupDropZone] = useState(false);



  const [statusUpdates, setStatusUpdates] = useState({});
  const contentTutorialState =
    user?.ui_preferences?.tutorials?.[CONTENT_TUTORIAL_PREFERENCE_KEY] || {};

  const markContentTutorialVisited = useCallback(async () => {
    if (contentTutorialState.visited_at) {
      return;
    }

    await updateUiPreferences({
      tutorials: {
        [CONTENT_TUTORIAL_PREFERENCE_KEY]: {
          visited_at: new Date().toISOString(),
        },
      },
    });
  }, [
    contentTutorialState.visited_at,
    updateUiPreferences,
  ]);

  const handleCloseContentTutorialIntro = useCallback(async () => {
    setShowTutorialIntroModal(false);
    setIsFirstTimeTutorialPrompt(false);

    try {
      await markContentTutorialVisited();
    } catch (error) {
      console.error("Error updating content tutorial visited state:", error);
    }
  }, [markContentTutorialVisited]);

  useEffect(() => {
    if (
      !tutorialsEnabled ||
      !canManageContent ||
      !user?.id ||
      isTutorialActive ||
      contentTutorialAutoPromptedRef.current
    ) {
      return;
    }

    if (contentTutorialState.visited_at) {
      return;
    }

    contentTutorialAutoPromptedRef.current = true;
    setIsFirstTimeTutorialPrompt(true);
    setShowTutorialIntroModal(true);
  }, [
    canManageContent,
    contentTutorialState.visited_at,
    isTutorialActive,
    tutorialsEnabled,
    user?.id,
  ]);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [contentToSchedule, setContentToSchedule] = useState(null);
  const [isScheduleSelectionMode, setIsScheduleSelectionMode] = useState(false);


  const [configsRefresh, setConfigsRefresh] = useState(0);
  const [piecesRefresh, setPiecesRefresh] = useState(0);
  const lastAppliedFilterIdRef = useRef(null);
  const urlConfigAppliedRef = useRef(false); // Track if we've applied a URL config param
  const urlLocalModeRef = useRef(false); // Track if the URL wants local (non-AppLayout) selection
  const urlThreadToOpenRef = useRef(null); // Thread ID from notification to open content modal
  const urlClientToOpenRef = useRef(null); // Client ID from notification (used to select config first)
  const urlThreadConsumedRef = useRef(true); // Ensures a notification thread is auto-opened only once
  const {
    scrollContainerRef,
    handleDragMoveAutoScroll,
    stopAutoScroll,
  } = useDragAutoScroll();

  const filterParam = useMemo(() => {
    const selectedId = selectedFilterData?.id;
    if (!selectedId || selectedId === "global") return null;
    if (user?.role === "superadmin") {
      return { key: "agency", value: selectedId };
    }
    return null;
  }, [selectedFilterData?.id, user?.role]);

  const configsEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (filterParam) {
      params.append(filterParam.key, filterParam.value);
    }
    const queryString = params.toString();
    return `content-generation/configs/${queryString ? `?${queryString}` : ""}`;
  }, [filterParam]);

  // Fetch clients (ContentGeneratorConfigs) from backend
  const { data: configsResponse, isLoading: isLoadingClients } = getData(
    configsEndpoint,
    interceptorReady,
    [configsRefresh, contentGeneratorRefreshKey, filterRefresh]
  );

  // Transform API response to clients list
  const realClients = useMemo(() => {
    if (!configsResponse) return [];
    const results = Array.isArray(configsResponse?.results)
      ? configsResponse.results
      : Array.isArray(configsResponse)
        ? configsResponse
        : [];
    return results.map((config) => ({
      id: config.id,
      clientId: config.client,
      name: config.client_name,
      logo: config.client_logo,
    }));
  }, [configsResponse]);
  const clients = useMemo(
    () => (tutorialList ? [CONTENT_TUTORIAL_MOCK_CLIENT, ...realClients] : realClients),
    [tutorialList, realClients]
  );

  const clientNameById = useMemo(() => {
    return clients.reduce((acc, client) => {
      if (client.clientId) {
        acc[client.clientId] = client.name;
      }
      return acc;
    }, {});
  }, [clients]);

  const clientNameByConfigId = useMemo(() => {
    return clients.reduce((acc, client) => {
      if (client.id) {
        acc[client.id] = client.name;
      }
      return acc;
    }, {});
  }, [clients]);

  const clientLogoByConfigId = useMemo(() => {
    return clients.reduce((acc, client) => {
      if (client.id && client.logo) {
        acc[client.id] = getAvatarImageUrl(client.logo);
      }
      return acc;
    }, {});
  }, [clients]);

  const clientIdByConfigId = useMemo(() => {
    return clients.reduce((acc, client) => {
      if (client.id && client.clientId) {
        acc[client.id] = client.clientId;
      }
      return acc;
    }, {});
  }, [clients]);

  const isContentReadOnly = (content) =>
    user?.role !== "client" &&
    isClientReadOnly(
      user,
      clientIdByConfigId[content.config ?? content.config_id ?? content.pieces?.[0]?.config]
    );

  const matchedClientConfigId = useMemo(() => {
    if (!isAgencyScopedUser || !selectedClientFilterId) return null;
    const matchedClient = clients.find(
      (client) => String(client.clientId) === String(selectedClientFilterId)
    );
    return matchedClient?.id ?? null;
  }, [clients, isAgencyScopedUser, selectedClientFilterId]);

  const effectiveConfigId =
    selectedClientFilterConfigId ?? matchedClientConfigId ?? null;
  const isAgencyScopedWithClientConfig = Boolean(
    isAgencyScopedUser && selectedClientFilterId && effectiveConfigId
  );
  // AppLayout client without content config: it has no contents, so the page stays empty
  // (the backend would fall back to every piece). Waits for the configs list before deciding.
  const isAppLayoutClientWithoutConfig = Boolean(
    isAgencyScopedUser && selectedClientFilterId && !effectiveConfigId && configsResponse
  );
  const isAppLayoutClientScoped = Boolean(isAgencyScopedUser && selectedClientFilterId);

  const resetContentViewForTutorial = useCallback(
    ({ tab = "all" } = {}) => {
      if (isAgencyScopedWithClientConfig) {
        changeSelectedFilter("global");
      }

      setSelectedTab(tab);
      setViewMode("table", { replace: true });
    },
    [changeSelectedFilter, isAgencyScopedWithClientConfig]
  );

  const handleOpenContentTutorialIntro = useCallback(() => {
    setIsFirstTimeTutorialPrompt(false);
    setShowTutorialIntroModal(true);
  }, []);

  const handleConfirmContentTutorial = useCallback(async () => {
    setShowTutorialIntroModal(false);
    setIsFirstTimeTutorialPrompt(false);
    resetContentViewForTutorial();
    try {
      await markContentTutorialVisited();
    } catch (error) {
      console.error("Error updating content tutorial visited state:", error);
    }

    if (isAgencyScopedWithClientConfig) {
      setPendingTutorialAction("start");
      return;
    }

    startTutorial();
  }, [
    isAgencyScopedWithClientConfig,
    markContentTutorialVisited,
    resetContentViewForTutorial,
    startTutorial,
  ]);

  useEffect(() => {
    if (!pendingTutorialAction || isAgencyScopedWithClientConfig) {
      return;
    }

    if (pendingTutorialAction === "start") {
      startTutorial();
    }

    setPendingTutorialAction(null);
  }, [isAgencyScopedWithClientConfig, pendingTutorialAction, startTutorial]);

  const selectedConfigId = useMemo(() => {
    if (tutorialList?.selectConfig) return CONTENT_TUTORIAL_MOCK_CONFIG_ID;
    if (isAgencyScopedWithClientConfig) {
      return effectiveConfigId;
    }

    if (isAppLayoutClientScoped || selectedTab === "all") return null;
    return selectedTab;
  }, [tutorialList, isAgencyScopedWithClientConfig, isAppLayoutClientScoped, effectiveConfigId, selectedTab]);

  // Auto-select config from URL query parameter (e.g., from notification click)
  useEffect(() => {
    const configParam = searchParams.get("config");
    const clientParam = searchParams.get("client");
    const pieceParam = searchParams.get("piece");
    const threadParam = searchParams.get("thread");
    const localParam = searchParams.get("local");
    const groupParam = searchParams.get("group");
    // Notification links to a group or a piece: open its page.
    if (groupParam) {
      navigate(`grupo/${groupParam}`, { replace: true });
      return;
    }
    if (pieceParam) {
      navigate(`pieza/${pieceParam}`, { replace: true });
      return;
    }
    if (configParam || clientParam || threadParam) {
      // Store the pending config ID in the ref (string to avoid type mismatches)
      urlConfigAppliedRef.current = configParam;
      urlClientToOpenRef.current = clientParam;
      urlThreadToOpenRef.current = threadParam;
      if (threadParam) {
        urlThreadConsumedRef.current = false;
      }
      urlLocalModeRef.current = localParam === "1";
      console.log(
        "ContentGenerator - Captured URL params:",
        {
          config: urlConfigAppliedRef.current,
          client: urlClientToOpenRef.current,
          thread: urlThreadToOpenRef.current,
        }
      );
      // Clear URL immediately to keep it clean
      setSearchParams({}, { replace: true });
    }

    let pendingConfigId = urlConfigAppliedRef.current;
    const pendingClientId = urlClientToOpenRef.current;

    if ((!pendingConfigId || pendingConfigId === true) && pendingClientId && clients.length > 0) {
      const matchingClientByClientId = clients.find(
        (client) => String(client.clientId) === String(pendingClientId)
      );
      if (matchingClientByClientId) {
        pendingConfigId = String(matchingClientByClientId.id);
        urlConfigAppliedRef.current = pendingConfigId;
      }
    }

    console.log(
      "ContentGenerator - Checking pending config:",
      pendingConfigId,
      "clients:",
      clients.length
    );
    if (pendingConfigId && pendingConfigId !== true && clients.length > 0) {
      const matchingClient = clients.find(
        (client) => String(client.id) === String(pendingConfigId)
      );
      console.log("ContentGenerator - Matching client:", matchingClient);
      if (matchingClient) {
        // For any role: prefer local (page-level) selection so we can keep AppLayout "Vista Global"
        if (isAgencyScopedUser && urlLocalModeRef.current) {
          changeSelectedFilter("global");
        }

        console.log("ContentGenerator - Applying selectedTab:", matchingClient.id);
        setSelectedTab(matchingClient.id);

        // Mark as applied by setting to true (not a string anymore)
        urlConfigAppliedRef.current = true;
        urlLocalModeRef.current = false;
      }
    }
  }, [clients, searchParams, setSearchParams, isAgencyScopedUser, changeSelectedFilter, navigate]);

  // Fetch content pieces for selected config or all pieces
  const shouldFetchContentPieces = !isAppLayoutClientWithoutConfig && !tutorialList;
  const shouldFetchCalendarAdaptations = viewMode === "calendar" && !isAppLayoutClientWithoutConfig;

  // Build API URL with filters and pagination
  const buildContentPiecesUrl = () => {
    if (!shouldFetchContentPieces) return null;

    const params = new URLSearchParams();

    // Add pagination parameters
    params.append("page", (page + 1).toString()); // Backend uses 1-based indexing
    params.append("page_size", pageSize.toString());

    if (isAgencyScopedWithClientConfig && selectedConfigId) {
      params.append("config", selectedConfigId);
    } else if (selectedTab !== "all") {
      // Add config filter only if NOT in "all" tab
      params.append("config", selectedTab);
    }

    // Add status filter if not "all"
    if (selectedStatus && selectedStatus !== "all") {
      // Map frontend status values to backend values
      if (selectedStatus === "review") {
        params.append("status", "client_review");
      } else if (selectedStatus === "accepted") {
        // Aceptado = aprobado y aún sin programar ni publicar.
        params.append("status", "client_approved");
        params.append("publication_status", "none");
      } else if (selectedStatus === "scheduled" || selectedStatus === "published") {
        params.append("publication_status", selectedStatus);
      } else {
        params.append("status", selectedStatus);
      }
    }

    // Add search term if present
    if (searchTerm) {
      params.append("search", searchTerm);
    }

    if (filterParam) {
      params.append(filterParam.key, filterParam.value);
    }

    const queryString = params.toString();
    return `content-generation/pieces/${queryString ? "?" + queryString : ""}`;
  };

  const { data: realContentPiecesResponse, isLoading: isLoadingContentPieces } =
    getData(buildContentPiecesUrl(), interceptorReady && shouldFetchContentPieces, [
      selectedTab,
      selectedClientFilterId,
      selectedStatus,
      searchTerm,
      page,
      pageSize,
      piecesRefresh,
      contentGeneratorRefreshKey,
      filterRefresh,
    ]);
  const contentPiecesResponse = tutorialList ? tutorialList.piecesResponse : realContentPiecesResponse;

  const buildCalendarAdaptationsUrl = () => {
    if (!shouldFetchCalendarAdaptations) return null;

    const params = new URLSearchParams();

    if (isAppLayoutClientScoped) {
      params.append("client", selectedClientFilterId);
    } else if (selectedTab !== "all") {
      const clientId = clientIdByConfigId[selectedTab];
      if (clientId) {
        params.append("client", clientId);
      } else {
        params.append("config", selectedTab);
      }
    }

    if (filterParam) {
      params.append(filterParam.key, filterParam.value);
    }

    params.append("scheduled", "true");

    const queryString = params.toString();
    return `content-generation/adaptations/${queryString ? "?" + queryString : ""}`;
  };

  const { data: calendarAdaptationsResponse } = getData(
    buildCalendarAdaptationsUrl(),
    interceptorReady && shouldFetchCalendarAdaptations,
    [
      selectedTab,
      selectedClientFilterId,
      viewMode,
      piecesRefresh,
      contentGeneratorRefreshKey,
      filterRefresh,
    ]
  );

  // Upcoming publications: next to the table on wide screens, above it otherwise.
  const isLgScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const upcomingLayout = isLgScreen ? "side" : "top";
  // Panel above the table: grow the page (it scrolls) instead of squeezing the table.
  const isUpcomingOnTop = upcomingLayout === "top";

  const upcomingFilters = useMemo(() => {
    const filters = {};
    if (isAppLayoutClientScoped) {
      filters.client = selectedClientFilterId;
    } else if (selectedTab !== "all") {
      const clientId = clientIdByConfigId[selectedTab];
      if (clientId) filters.client = clientId;
      else filters.config = selectedTab;
    }
    if (filterParam) filters[filterParam.key] = filterParam.value;
    return filters;
  }, [isAppLayoutClientScoped, selectedClientFilterId, selectedTab, clientIdByConfigId, filterParam]);

  // Use delayed loading to avoid skeleton flicker on fast loads
  const showTableSkeleton = useDelayedLoading(isLoadingContentPieces, 150);
  const hasLoadedContentPieces =
    isAppLayoutClientWithoutConfig ||
    (contentPiecesResponse !== undefined && contentPiecesResponse !== null);

  // Nothing is fetched for a client without config: clear the previous client's counters
  useEffect(() => {
    if (!isAppLayoutClientWithoutConfig) return;
    setTotalCount(0);
    setStatusStats({ draft: 0, client_review: 0, client_approved: 0, scheduled: 0, published: 0 });
  }, [isAppLayoutClientWithoutConfig]);

  // Update total count and stats from paginated response
  useEffect(() => {
    if (contentPiecesResponse?.count !== undefined) {
      setTotalCount(contentPiecesResponse.count);
    }
    // Update status stats if provided by backend
    if (contentPiecesResponse?.status_stats) {
      setStatusStats(contentPiecesResponse.status_stats);
    }
  }, [contentPiecesResponse]);

  // A batch is being generated for this client: refresh the table so its pieces show up.
  const isBatchGenerating = Boolean(contentPiecesResponse?.is_generating_campaign);
  useEffect(() => {
    if (!isBatchGenerating) return;
    const interval = setInterval(() => setPiecesRefresh((prev) => prev + 1), 5000);
    return () => clearInterval(interval);
  }, [isBatchGenerating]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedTab, selectedClientFilterId, selectedStatus, searchTerm]);

  // Transform content pieces response and filter out deleted items
  const contentPieces = useMemo(() => {
    if (!contentPiecesResponse) return [];
    const pieces = Array.isArray(contentPiecesResponse?.results)
      ? contentPiecesResponse.results
      : Array.isArray(contentPiecesResponse)
        ? contentPiecesResponse
        : [];

    // Apply local status updates, including grouped pieces.
    return pieces.map((piece) => {
      if (isGroupEntry(piece)) {
        return {
          ...piece,
          pieces: (piece.pieces || []).map((groupPiece) => ({
            ...groupPiece,
            status: statusUpdates[groupPiece.id] ?? groupPiece.status,
            unopened_unread_notifications: groupPiece.unopened_unread_notifications || 0,
          })),
        };
      }

      return {
        ...piece,
        status: statusUpdates[piece.id] ?? piece.status,
        unopened_unread_notifications: piece.unopened_unread_notifications || 0,
      };
    });
  }, [contentPiecesResponse, statusUpdates]);

  const flatContentPieces = useMemo(
    () =>
      contentPieces.flatMap((piece) =>
        isGroupEntry(piece) ? piece.pieces || [] : [piece]
      ),
    [contentPieces]
  );

  const selectableDraftIdsOnPage = useMemo(
    () =>
      new Set(
        flatContentPieces
          .filter(
            (piece) =>
              piece.status === "draft" &&
              !piece.group_id &&
              piece.has_platform_adaptations
          )
          .map((piece) => String(piece.id))
      ),
    [flatContentPieces]
  );

  const flatPiecesById = useMemo(
    () =>
      flatContentPieces.reduce((acc, piece) => {
        acc[String(piece.id)] = piece;
        return acc;
      }, {}),
    [flatContentPieces]
  );

  const groupsById = useMemo(
    () =>
      contentPieces
        .filter((item) => isGroupEntry(item))
        .reduce((acc, group) => {
          acc[String(group.id)] = group;
          return acc;
        }, {}),
    [contentPieces]
  );

  const showDragColumn =
    canManageContent && !isGroupingMode && Boolean(selectedConfigId);
  // Con un cliente seleccionado la columna "Cliente" es redundante.
  const showClientColumn = !isAppLayoutClientScoped && selectedTab === "all";
  const totalTableColumns =
    6 + (showClientColumn ? 1 : 0) + (isGroupingMode ? 1 : 0) + (showDragColumn ? 1 : 0);

  // Cabeceras de la tabla de contenidos (las filas las pintan PieceRow/GroupRow).
  const contentTableColumns = [
    {
      key: "select",
      header: null,
      hidden: !isGroupingMode,
      width: 44,
      headerSx: {
        paddingLeft: { xs: "6px !important", md: "16px !important" },
        paddingRight: "0 !important",
      },
    },
    {
      key: "drag",
      header: null,
      hidden: !showDragColumn,
      width: 32,
      headerSx: { p: "0 !important", textAlign: "center" },
    },
    {
      key: "content",
      header: "Contenido",
      minWidth: { xs: 250, md: 380 },
      headerSx: { paddingLeft: { xs: "2px !important", md: "8px !important" } },
    },
    { key: "platform", header: "Plataforma" },
    { key: "status", header: "Estado" },
    {
      key: "client",
      header: isSuperAdmin ? "Cliente/Agencia" : "Cliente",
      hidden: !showClientColumn,
    },
    { key: "scheduled_at", header: "Fecha de programación" },
    { key: "created_at", header: "Fecha de creación" },
    { key: "actions", header: "Acciones", align: "center", width: 50 },
  ];

  // Clientes no ven borradores; internos sí. Programado/Publicado no cuentan como Aceptado.
  const lifecycleTabs = [
    { value: "review", label: "Revisión", count: statusStats.client_review ?? 0 },
    { value: "accepted", label: "Aceptado", count: statusStats.client_approved ?? 0 },
    { value: "scheduled", label: "Programado", count: statusStats.scheduled ?? 0 },
    { value: "published", label: "Publicado", count: statusStats.published ?? 0 },
  ];
  const visibleStatusTabs = isClientUser
    ? lifecycleTabs
    : [{ value: "draft", label: "Borrador", count: statusStats.draft ?? 0 }, ...lifecycleTabs];
  const contentStatusTabs = [
    {
      value: "all",
      label: "Todos",
      count: visibleStatusTabs.reduce((total, tab) => total + tab.count, 0),
    },
    ...visibleStatusTabs,
  ];

  const getDragDisabledReason = useCallback(
    (piece) => {
      if (!canManageContent) {
        return "No tienes permisos para reorganizar contenidos.";
      }
      if (isGroupingMode) {
        return "Desactiva el modo de agrupación para arrastrar contenidos.";
      }
      if (piece?.status !== "draft") {
        return "Solo se pueden arrastrar contenidos en estado Borrador.";
      }
      if (!piece.group_id && !piece.has_platform_adaptations) {
        return "Solo se pueden agrupar contenidos con al menos una adaptación.";
      }
      return null;
    },
    [canManageContent, isGroupingMode]
  );

  const canDropPieceIntoGroup = useCallback(
    (piece, group) => {
      if (!piece || !group) return false;
      if (piece.status !== "draft") return false;
      if (group.status !== "draft") return false;
      if (!piece.has_platform_adaptations) return false;
      if (String(piece.group_id || "") === String(group.id)) return false;
      return true;
    },
    []
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const availableGroupIds = new Set(
        contentPieces
          .filter((piece) => isGroupEntry(piece) && piece.id != null)
          .map((piece) => String(piece.id))
      );

      let changed = false;
      const next = { ...prev };

      availableGroupIds.forEach((groupId) => {
        if (next[groupId] === undefined) {
          next[groupId] = false;
          changed = true;
        }
      });

      Object.keys(next).forEach((groupId) => {
        if (!availableGroupIds.has(groupId)) {
          delete next[groupId];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [contentPieces]);

  useEffect(() => {
    if (!isGroupingMode) return;
    setSelectedGroupPieceIds((prev) =>
      prev.filter((id) => selectableDraftIdsOnPage.has(String(id)))
    );
  }, [isGroupingMode, selectableDraftIdsOnPage]);

  useEffect(() => {
    const pendingThreadId = urlThreadToOpenRef.current;
    if (!pendingThreadId || urlThreadConsumedRef.current) return;

    const matchingPiece = flatContentPieces.find(
      (piece) => String(piece.thread_id) === String(pendingThreadId)
    );

    if (!matchingPiece) return;

    urlThreadConsumedRef.current = true;
    urlThreadToOpenRef.current = null;
    urlClientToOpenRef.current = null;
    navigate(`pieza/${matchingPiece.id}`, { replace: true });
  }, [flatContentPieces, navigate]);

  const calendarAdaptations = useMemo(() => {
    if (!calendarAdaptationsResponse) return [];
    const adaptations = Array.isArray(calendarAdaptationsResponse?.results)
      ? calendarAdaptationsResponse.results
      : Array.isArray(calendarAdaptationsResponse)
        ? calendarAdaptationsResponse
        : [];

    return adaptations.filter((adaptation) => adaptation.scheduled_at);
  }, [calendarAdaptationsResponse]);



  const scheduleContentOptions = useMemo(() => {
    return flatContentPieces.map((piece) => {
      const title = piece.title || `Contenido #${piece.id}`;
      const clientName =
        piece.client_name ||
        piece.clientName ||
        (piece.client && clientNameById[piece.client]) ||
        (piece.client_id && clientNameById[piece.client_id]) ||
        (piece.config && clientNameByConfigId[piece.config]) ||
        (piece.config_id && clientNameByConfigId[piece.config_id]) ||
        (piece.configId && clientNameByConfigId[piece.configId]);
      const label = clientName ? `${title} · ${clientName}` : title;

      return { value: String(piece.id), label };
    });
  }, [flatContentPieces, clientNameById, clientNameByConfigId]);

  // Menu state
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [selectedContentForMenu, setSelectedContentForMenu] = useState(null);

  React.useEffect(() => {
    if (!isAgencyScopedUser) return;

    // Skip if we have a pending or just-applied URL config (from notification click)
    // urlConfigAppliedRef can be: false (not set), a string id (pending), or true (just applied)
    if (urlConfigAppliedRef.current) {
      // Only reset if it was just applied (true), not if still pending (number)
      if (urlConfigAppliedRef.current === true) {
        urlConfigAppliedRef.current = false;
      }
      return;
    }

    const currentFilterId = selectedFilterData?.id ?? "global";
    if (lastAppliedFilterIdRef.current === null) {
      lastAppliedFilterIdRef.current = currentFilterId;
      return;
    }
    const filterChanged = lastAppliedFilterIdRef.current !== currentFilterId;

    if (!filterChanged) return;

    setViewMode("table", { replace: true });
    setPiecesRefresh((prev) => prev + 1);

    lastAppliedFilterIdRef.current = currentFilterId;
  }, [isAgencyScopedUser, selectedFilterData?.id]);

  const selectedClient = useMemo(
    () => (selectedConfigId ? clients.find((c) => c.id === selectedConfigId) : null),
    [selectedConfigId, clients]
  );

  const selectedClientName = useMemo(() => {
    if (isAppLayoutClientScoped) {
      return selectedClient?.name || selectedClientFilterName || "Cliente";
    }
    if (selectedTab === "all") return null;
    return selectedClient?.name || "Cliente";
  }, [
    isAppLayoutClientScoped,
    selectedClientFilterName,
    selectedClient?.name,
    selectedTab,
  ]);

  // No need for client-side filtering or pagination - backend handles everything
  // Use contentPieces directly (already paginated by server)

  // Handlers
  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === "" && lastSearchRef.current !== "") {
        lastSearchRef.current = "";
        setSearchTerm("");
        setPage(0);
        return;
      }

      // CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
        setPage(0);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const handleStatusChange = useCallback((status) => {
    setSelectedStatus(status);
    setPage(0);
  }, []);

  const totalPages = totalCount > -1 ? Math.ceil(totalCount / pageSize) : null;
  const canGoBack = page > 0 && !isLoadingContentPieces;
  const canGoNext =
    !isLoadingContentPieces &&
    (totalPages !== null
      ? page < totalPages - 1
      : Boolean(contentPiecesResponse?.next));

  const handleChangePage = (newPage) => {
    if (isLoadingContentPieces || newPage === page) return;
    if (newPage > page && !canGoNext) return;
    if (newPage < page && !canGoBack) return;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  // The create modal starts on the AppLayout client, or on the one picked in the page filter.
  const createContentDefaultClientId =
    selectedClientFilterId ?? clientIdByConfigId[selectedConfigId] ?? null;

  const handleCreateContentContinue = ({ configId, type, title, isNewConfig }) => {
    setShowCreateContentModal(false);
    if (isNewConfig) setConfigsRefresh((prev) => prev + 1);
    if (type === "manual") {
      navigate(`manual?config=${configId}&titulo=${encodeURIComponent(title)}`);
      return;
    }
    navigate(`generar?config=${configId}${type === "campaign" ? "" : "&tipo=individual"}`);
  };

  const handleOpenCreateContentModal = useCallback(() => {
    setShowCreateContentModal(true);
  }, []);

  const handleCloseCreateContentModal = () => {
    setShowCreateContentModal(false);
  };

  const handleContentPieceClick = (content, adaptationId = null) => {
    // Tutorial rows are mock data with no page behind them.
    if (tutorialList) return;
    // A grouped piece opens its group's joint view, on the summary tab.
    if (content.group_id) {
      navigate(`grupo/${content.group_id}`);
      return;
    }
    navigate(`pieza/${content.id}${adaptationId ? `?adaptation=${adaptationId}` : ""}`);
  };

  const handleMenuOpen = (event, content) => {
    event.stopPropagation();
    const anchorElement = event.currentTarget;
    if (anchorElement && typeof anchorElement.getBoundingClientRect === "function") {
      const rect = anchorElement.getBoundingClientRect();
      setMenuAnchorPosition({
        top: Math.round(rect.bottom),
        left: Math.round(rect.right),
      });
    } else {
      setMenuAnchorPosition(null);
    }
    setSelectedContentForMenu(content);
  };

  const handleMenuClose = () => {
    setMenuAnchorPosition(null);
    setSelectedContentForMenu(null);
  };

  const handleSendToReview = () => {
    if (!selectedContentForMenu) return;
    setShowSendToClientModal(true);
  };

  const handleConfirmSendToReview = async () => {
    if (!selectedContentForMenu) return;

    setIsSaving(true);
    setShowSendToClientModal(false);

    const targetGroupId = isGroupEntry(selectedContentForMenu)
      ? selectedContentForMenu.id
      : selectedContentForMenu.group_id;

    try {
      if (targetGroupId) {
        await apiClient.post(
          `content-generation/groups/${targetGroupId}/send_to_review/`
        );
        setPiecesRefresh((prev) => prev + 1);
        setPopupMessage("Grupo enviado a revisión correctamente");
      } else {
        await apiClient.post(
          `content-generation/pieces/${selectedContentForMenu.id}/send_to_review/`
        );
        setStatusUpdates((prev) => ({
          ...prev,
          [selectedContentForMenu.id]: "client_review",
        }));
        setPopupMessage("Contenido enviado a revisión correctamente");
      }
      setIsPopupError(false);
      setShowSuccessPopup(true);
      handleMenuClose();
    } catch (error) {
      console.error("Error sending content to review:", error);
      setPopupMessage(
        error.response?.data?.error ||
        "Error al enviar a revisión. Inténtalo de nuevo."
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveContent = () => {
    if (!selectedContentForMenu) return;
    setShowApproveContentModal(true);
  };

  const handleConfirmApproveContent = async () => {
    if (!selectedContentForMenu) return;

    setIsSaving(true);
    setShowApproveContentModal(false);

    const targetGroupId = isGroupEntry(selectedContentForMenu)
      ? selectedContentForMenu.id
      : selectedContentForMenu.group_id;

    try {
      if (targetGroupId) {
        await apiClient.post(
          `content-generation/groups/${targetGroupId}/approve/`
        );
        setPiecesRefresh((prev) => prev + 1);
        setPopupMessage("Grupo marcado como aceptado correctamente");
      } else {
        await apiClient.post(
          `content-generation/pieces/${selectedContentForMenu.id}/approve/`
        );
        setStatusUpdates((prev) => ({
          ...prev,
          [selectedContentForMenu.id]: "client_approved",
        }));
        setPopupMessage("Contenido marcado como aceptado correctamente");
      }
      setIsPopupError(false);
      setShowSuccessPopup(true);
      handleMenuClose();
    } catch (error) {
      console.error("Error approving content:", error);
      setPopupMessage(
        error.response?.data?.error ||
        "Error al marcar como aceptado. Inténtalo de nuevo."
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenScheduleModal = () => {
    setIsScheduleSelectionMode(true);
    setContentToSchedule(null);
    setScheduleDateTime("");
    setShowScheduleModal(true);
  };

  const handleScheduleContentSelect = (value) => {
    const selectedContent = flatContentPieces.find(
      (piece) => String(piece.id) === String(value)
    );
    setContentToSchedule(selectedContent || null);
    setScheduleDateTime(formatDateTimeInputValue(selectedContent?.scheduled_at));
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setContentToSchedule(null);
    setScheduleDateTime("");
    setIsScheduleSelectionMode(false);
  };

  const handleConfirmSchedule = async () => {
    if (!contentToSchedule || !scheduleDateTime) return;

    const targetContent = contentToSchedule;
    setShowScheduleModal(false);
    setContentToSchedule(null);
    setIsScheduleSelectionMode(false);

    try {
      // The date is the piece's (all its networks share it).
      await updatePieceScheduledAt(
        targetContent.id,
        new Date(scheduleDateTime).toISOString()
      );
      setPiecesRefresh((prev) => prev + 1);

      setPopupMessage("Fecha guardada. Prográmalo en las redes desde el contenido.");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error scheduling content:", error);
      const errorMessage = publishingErrorText(
        error,
        "Error al programar el contenido. Inténtalo de nuevo."
      );
      setPopupMessage(errorMessage);
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setScheduleDateTime("");
    }
  };

  const handleEditContent = () => {
    if (!selectedContentForMenu) return;
    handleMenuClose();
    handleContentPieceClick(selectedContentForMenu);
  };

  const handleDeleteContent = () => {
    if (!selectedContentForMenu) return;
    // Published pieces are not deleted: networks' APIs can't remove a published post.
    if (!isGroupEntry(selectedContentForMenu) && selectedContentForMenu.publication_status === "published") {
      handleMenuClose();
      setPopupMessage(
        "Este contenido ya está publicado en redes y no se puede eliminar. Si quieres retirarlo, bórralo manualmente desde cada red."
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
      return;
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedContentForMenu) return;

    const targetIsGroup = isGroupEntry(selectedContentForMenu);
    const targetId = selectedContentForMenu.id;

    // Close modal immediately
    setShowDeleteModal(false);
    handleMenuClose();

    try {
      if (targetIsGroup) {
        await apiClient.delete(`content-generation/groups/${targetId}/`);
        setPopupMessage("Grupo eliminado. Los contenidos del grupo siguen disponibles de forma independiente.");
      } else {
        await apiClient.delete(`content-generation/pieces/${targetId}/`);
        setPopupMessage("Contenido eliminado exitosamente");
      }

      // Refresh pieces list to reflect the deletion
      setPiecesRefresh((prev) => prev + 1);

      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error deleting content:", error);

      const errorMessage = publishingErrorText(
        error,
        targetIsGroup
          ? "Error al eliminar el grupo. Inténtalo de nuevo."
          : "Error al eliminar el contenido. Inténtalo de nuevo."
      );
      setPopupMessage(errorMessage);
      setIsPopupError(true);
      setShowSuccessPopup(true);
    }
  };

  const menuOpen = Boolean(menuAnchorPosition && selectedContentForMenu);
  const isMenuTargetGroup = isGroupEntry(selectedContentForMenu);
  const normalizedMenuStatus = selectedContentForMenu?.status
    ? selectedContentForMenu.status === "client_review"
      ? "review"
      : selectedContentForMenu.status === "client_approved"
        ? "accepted"
        : selectedContentForMenu.status
    : null;
  const canEditContent =
    canManageContent &&
    !isMenuTargetGroup &&
    (normalizedMenuStatus === "draft" || normalizedMenuStatus === "review");
  const canSendToClient =
    canManageContent &&
    normalizedMenuStatus === "draft" &&
    (isMenuTargetGroup
      ? selectedContentForMenu?.pieces?.every((p) => p.has_platform_adaptations)
      : selectedContentForMenu?.has_platform_adaptations === true);
  const canApproveContent =
    canManageContent && normalizedMenuStatus === "review";
    canManageContent &&
    ["draft", "review", "accepted"].includes(normalizedMenuStatus);
  const canDeleteContent = isSuperAdmin || isAgencyScopedUser;
  // A group has no config of its own: it takes its pieces' one.
  const menuTargetConfigId = isMenuTargetGroup
    ? selectedContentForMenu?.pieces?.[0]?.config
    : selectedContentForMenu?.config;
  const menuPersonasTarget =
    canManageContent && menuTargetConfigId
      ? {
        configId: menuTargetConfigId,
        clientName:
          selectedContentForMenu?.client_name ||
          clientNameByConfigId[menuTargetConfigId] ||
          "cliente",
      }
      : null;

  const handleEditBuyerPersonas = () => {
    setPersonasEditTarget(menuPersonasTarget);
    handleMenuClose();
  };
  const toggleGroupExpansion = useCallback((groupId) => {
    if (groupId == null) return;
    setExpandedGroups((prev) => {
      const key = String(groupId);
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  }, []);

  const handleStartGroupingMode = () => {
    setIsGroupingMode(true);
    setSelectedGroupPieceIds([]);
  };

  const handleCancelGroupingMode = () => {
    setIsGroupingMode(false);
    setSelectedGroupPieceIds([]);
    setShowCreateGroupModal(false);
    setNewGroupName("");
  };

  const togglePieceSelection = (pieceId) => {
    const pieceKey = String(pieceId);
    if (!selectableDraftIdsOnPage.has(pieceKey)) return;
    setSelectedGroupPieceIds((prev) =>
      prev.includes(pieceKey)
        ? prev.filter((id) => id !== pieceKey)
        : [...prev, pieceKey]
    );
  };

  const handleOpenCreateGroupModal = () => {
    if (!selectedGroupPieceIds.length) return;
    setNewGroupName("");
    setShowCreateGroupModal(true);
  };

  const handleCreateGroup = async () => {
    if (!selectedGroupPieceIds.length) return;
    const normalizedName = newGroupName.trim();
    if (!normalizedName) {
      setPopupMessage("Debes indicar un nombre para el grupo.");
      setIsPopupError(true);
      setShowSuccessPopup(true);
      return;
    }

    setIsCreatingGroup(true);
    try {
      await apiClient.post("content-generation/groups/", {
        name: normalizedName,
        piece_ids: selectedGroupPieceIds.map((id) => Number(id)),
      });
      setPopupMessage("Contenidos agrupados correctamente.");
      setIsPopupError(false);
      setShowSuccessPopup(true);
      setShowCreateGroupModal(false);
      setIsGroupingMode(false);
      setSelectedGroupPieceIds([]);
      setNewGroupName("");
      setPiecesRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error creating group:", error);
      setPopupMessage(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "No se pudieron agrupar los contenidos."
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const clearContentDragState = useCallback(() => {
    stopAutoScroll();
    setDraggedPieceId(null);
    setDragOverGroupId(null);
    setIsOverUngroupDropZone(false);
  }, [stopAutoScroll]);

  useEffect(() => {
    if (isGroupingMode || !showDragColumn) {
      clearContentDragState();
    }
  }, [clearContentDragState, isGroupingMode, showDragColumn]);

  useEffect(() => {
    if (!draggedPieceId) return undefined;

    const forceReset = () => {
      // Defer one frame so native drag/drop lifecycle can finish first.
      requestAnimationFrame(() => {
        clearContentDragState();
      });
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        forceReset();
      }
    };

    window.addEventListener("mouseup", forceReset, true);
    window.addEventListener("touchend", forceReset, true);
    window.addEventListener("dragend", forceReset, true);
    window.addEventListener("drop", forceReset, true);
    window.addEventListener("blur", forceReset, true);
    window.addEventListener("keydown", handleEscape, true);

    return () => {
      window.removeEventListener("mouseup", forceReset, true);
      window.removeEventListener("touchend", forceReset, true);
      window.removeEventListener("dragend", forceReset, true);
      window.removeEventListener("drop", forceReset, true);
      window.removeEventListener("blur", forceReset, true);
      window.removeEventListener("keydown", handleEscape, true);
    };
  }, [clearContentDragState, draggedPieceId]);

  const openAddOrMoveToGroupConfirmation = useCallback(
    ({ piece, targetGroup }) => {
      if (!piece || !targetGroup) return;
      const sourceGroupId = piece.group_id ? String(piece.group_id) : null;
      const sourceGroup = sourceGroupId ? groupsById[sourceGroupId] : null;

      setPendingGroupMembershipAction({
        action: "add_to_group",
        piece,
        source_group_id: sourceGroup?.id ?? piece.group_id ?? null,
        source_group_name:
          sourceGroup?.name || piece.group_name || null,
        target_group_id: targetGroup.id,
        target_group_name: targetGroup.name || `Grupo #${targetGroup.id}`,
      });
    },
    [groupsById]
  );

  const openRemoveFromGroupConfirmation = useCallback(
    ({ piece }) => {
      if (!piece?.group_id) return;
      const sourceGroup = groupsById[String(piece.group_id)];

      setPendingGroupMembershipAction({
        action: "remove_from_group",
        piece,
        source_group_id: sourceGroup?.id ?? piece.group_id,
        source_group_name:
          sourceGroup?.name || piece.group_name || `Grupo #${piece.group_id}`,
      });
    },
    [groupsById]
  );

  const handleCloseGroupMembershipModal = useCallback(() => {
    if (isSaving) return;
    setPendingGroupMembershipAction(null);
  }, [isSaving]);

  const handleConfirmGroupMembershipAction = async () => {
    if (!pendingGroupMembershipAction?.piece?.id) return;

    const actionType = pendingGroupMembershipAction.action;
    const pieceId = Number(pendingGroupMembershipAction.piece.id);
    const sourceGroupId = pendingGroupMembershipAction.source_group_id
      ? Number(pendingGroupMembershipAction.source_group_id)
      : null;
    const targetGroupId = pendingGroupMembershipAction.target_group_id
      ? Number(pendingGroupMembershipAction.target_group_id)
      : null;

    setIsSaving(true);
    try {
      if (actionType === "add_to_group") {
        if (!targetGroupId) {
          throw new Error("No se encontró el grupo destino.");
        }

        if (sourceGroupId && sourceGroupId !== targetGroupId) {
          await apiClient.post(
            `content-generation/groups/${sourceGroupId}/remove_pieces/`,
            { piece_ids: [pieceId] }
          );
        }

        await apiClient.post(
          `content-generation/groups/${targetGroupId}/add_pieces/`,
          { piece_ids: [pieceId] }
        );

        if (sourceGroupId && sourceGroupId !== targetGroupId) {
          setPopupMessage("Contenido movido al grupo correctamente.");
        } else {
          setPopupMessage("Contenido añadido al grupo correctamente.");
        }
      } else if (actionType === "remove_from_group") {
        if (!sourceGroupId) {
          throw new Error("No se encontró el grupo de origen.");
        }

        await apiClient.post(
          `content-generation/groups/${sourceGroupId}/remove_pieces/`,
          { piece_ids: [pieceId] }
        );

        setPopupMessage("Contenido quitado del grupo correctamente.");
      } else {
        throw new Error("Acción de grupo no válida.");
      }

      setPendingGroupMembershipAction(null);
      setPiecesRefresh((prev) => prev + 1);
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error updating group membership:", error);
      setPopupMessage(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "No se pudo actualizar la pertenencia al grupo."
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderPlatformIcons = (platforms) => (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      {(platforms || []).map((platform) => {
        const meta = PLATFORM_META[platform];
        if (!meta) return null;
        const { Icon, color, label } = meta;
        return (
          <Tooltip key={platform} title={label} arrow>
            <Icon sx={{ fontSize: 16, color }} />
          </Tooltip>
        );
      })}
      {(!platforms || platforms.length === 0) && (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      )}
    </Box>
  );

  const draggedPiece = draggedPieceId
    ? flatPiecesById[String(draggedPieceId)] || null
    : null;
  const shouldShowUngroupDropZone = Boolean(
    showDragColumn && draggedPiece?.group_id && getDragDisabledReason(draggedPiece) == null
  );

  const PieceRow = ({ content, rowOptions = {} }) => {
    const { nested = false, keyPrefix = "piece", disableMenu = false } = rowOptions;
    const rowRef = useRef(null);
    const dragHandleRef = useRef(null);

    const statusInfo = getPieceDisplayStatus(content);
    const isSelectableDraft =
      content.status === "draft" && !content.group_id && content.has_platform_adaptations;
    const groupSelectDisabledReason =
      content.status !== "draft"
        ? "Solo puede agruparse el contenido en estado \"Borrador\"."
        : "Solo pueden agruparse contenidos con al menos una adaptación.";
    const isChecked = selectedGroupPieceIds.includes(String(content.id));
    const canShowRowMenu = canManageContent && !isGroupingMode && !disableMenu;
    const dragDisabledReason = getDragDisabledReason(content);
    const canDragRow = showDragColumn && !dragDisabledReason;
    const isRowDragging = String(draggedPieceId || "") === String(content.id);

    useEffect(() => {
      if (!canDragRow) return undefined;

      const element = rowRef.current;
      const dragHandle = dragHandleRef.current;
      if (!element || !dragHandle) return undefined;

      return draggable({
        element,
        dragHandle,
        getInitialData: () => ({
          entityType: "content-piece",
          pieceId: content.id,
          pieceStatus: content.status,
          pieceGroupId: content.group_id || null,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage, location, source }) => {
          mountDragPreview({
            nativeSetDragImage,
            element: source.element,
            input: location.current.input,
          });
        },
        onDragStart: () => {
          setDraggedPieceId(String(content.id));
          setDragOverGroupId(null);
          setIsOverUngroupDropZone(false);
        },
        onDrag: ({ location }) => {
          const clientY = location.current.input?.clientY;
          if (typeof clientY === "number") {
            handleDragMoveAutoScroll(clientY);
          }
        },
        onDrop: () => {
          clearContentDragState();
        },
      });
    }, [
      canDragRow,
      content.group_id,
      content.id,
      content.status,
      clearContentDragState,
      handleDragMoveAutoScroll,
    ]);

    return (
      <TableRow
        ref={rowRef}
        key={`${keyPrefix}-${content.id}`}
        onClick={() => {
          if (isGroupingMode) {
            if (isSelectableDraft) togglePieceSelection(content.id);
            return;
          }
          handleContentPieceClick(content);
        }}
        sx={{
          cursor: isGroupingMode ? (isSelectableDraft ? "pointer" : "default") : "pointer",
          backgroundColor: nested ? neutrals.gray[50] : "transparent",
          opacity: isRowDragging ? 0.48 : 1,
          "& > td:first-of-type": {
            borderLeft: nested ? `3px solid ${colors.primary.extralight}` : "3px solid transparent",
          },
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          transition: "background-color 0.2s ease",
        }}
      >
        {isGroupingMode && (
          <TableCell
            align="center"
            sx={{
              width: 44,
              paddingLeft: { xs: "6px !important", md: "16px !important" },
              paddingRight: "0 !important",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {isSelectableDraft ? (
              <Checkbox
                size="small"
                checked={isChecked}
                onChange={() => togglePieceSelection(content.id)}
                sx={{ p: 0.5 }}
              />
            ) : content.group_id ? null : (
              <Tooltip title={groupSelectDisabledReason} arrow>
                <span style={{ display: "inline-flex" }}>
                  <Checkbox
                    size="small"
                    disabled
                    sx={{ p: 0.5 }}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
        )}
        {showDragColumn && (
          <TableCell
            align="center"
            sx={{
              width: 32,
              p: "0 !important",
              textAlign: "center",
              verticalAlign: "middle",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Tooltip
              title={
                dragDisabledReason ||
                (content.group_id
                  ? "Arrastra para mover este contenido entre grupos o quitarlo del grupo."
                  : "Arrastra este contenido a una fila de grupo para añadirlo.")
              }
              arrow
            >
              <span style={{ display: "inline-flex" }}>
                <Box
                  ref={canDragRow ? dragHandleRef : null}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    height: "100%",
                    minHeight: 28,
                    color: canDragRow
                      ? theme.palette.text.secondary
                      : theme.palette.action.disabled,
                    cursor: canDragRow ? "grab" : "not-allowed",
                    "&:active": {
                      cursor: canDragRow ? "grabbing" : "not-allowed",
                    },
                  }}
                >
                  <GripVertical size={16} />
                </Box>
              </span>
            </Tooltip>
          </TableCell>
        )}
        <TableCell
          sx={{
            paddingLeft: { xs: "2px !important", md: "8px !important" },
            minWidth: { xs: 250, md: 380 },
          }}
        >
          <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ minWidth: 0, maxWidth: { xs: 180, md: 360 } }}>
              <Tooltip title={content.title} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pl: nested ? 2 : 0,
                    fontSize: {
                      xs: "0.75rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  {content.title}
                </Typography>
              </Tooltip>
            </Box>
            <UnreadThreadBadge count={content.unopened_unread_notifications} />
          </Box>
        </TableCell>
        <TableCell>{renderPlatformIcons(content.generated_platforms)}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Badge variant="outline" color={statusInfo.color}>
              {statusInfo.label}
            </Badge>
            {isContentReadOnly(content) && <ReadOnlyBadge />}
          </Box>
        </TableCell>
        {showClientColumn && (
          <TableCell
            sx={{
              paddingLeft: { xs: "2px !important", md: "8px !important" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Tooltip title={content.client_name || "Sin cliente"} arrow>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    size="sm"
                    name={content.client_name || "Cliente"}
                    src={getAvatarImageUrl(content.client_logo) || undefined}
                  />
                  <Typography
                    variant="caption"
                    color="text.primary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: {
                        xs: "0.7rem",
                        md: "0.75rem",
                      },
                    }}
                  >
                    {content.client_name || "Sin cliente"}
                  </Typography>
                </Box>
              </Tooltip>
              {isSuperAdmin && content.agency_name && (
                <Tooltip title={content.agency_name} arrow>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <AgencyIcon
                      size={19}
                      color={theme.palette.text.secondary}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: {
                          xs: "0.7rem",
                          md: "0.75rem",
                        },
                      }}
                    >
                      {content.agency_name}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        )}
        <DateCell value={content.scheduled_at} icon={Calendar} />
        <DateCell value={content.created_at} />
        <TableCell align="center">
          {canShowRowMenu && (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, content)}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <MoreVertical size={16} />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const GroupRow = ({ content }) => {
    const groupId = String(content.id);
    const rowRef = useRef(null);
    const isExpanded = Boolean(expandedGroups[groupId]);
    const groupStatusInfo = getGroupDisplayStatus(content);
    const groupPieces = content.pieces || [];
    const isDropActive = dragOverGroupId === groupId;

    useEffect(() => {
      if (!showDragColumn) return undefined;

      const element = rowRef.current;
      if (!element) return undefined;

      return dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          if (isGroupingMode) return false;
          if (source.data?.entityType !== "content-piece") return false;
          const sourcePiece = flatPiecesById[String(source.data?.pieceId)];
          return canDropPieceIntoGroup(sourcePiece, content);
        },
        getData: () => ({
          entityType: "content-group-target",
          groupId: content.id,
        }),
        onDragEnter: () => {
          setDragOverGroupId(groupId);
        },
        onDrag: () => {
          setDragOverGroupId(groupId);
        },
        onDragLeave: () => {
          setDragOverGroupId((prev) => (prev === groupId ? null : prev));
        },
        onDrop: ({ source }) => {
          const sourcePiece = flatPiecesById[String(source.data?.pieceId)];
          clearContentDragState();
          if (!canDropPieceIntoGroup(sourcePiece, content)) return;
          openAddOrMoveToGroupConfirmation({
            piece: sourcePiece,
            targetGroup: content,
          });
        },
      });
    }, [
      canDropPieceIntoGroup,
      clearContentDragState,
      content,
      flatPiecesById,
      groupId,
      isGroupingMode,
      openAddOrMoveToGroupConfirmation,
      showDragColumn,
    ]);

    return (
      <React.Fragment key={`group-${groupId}`}>
        <TableRow
          ref={rowRef}
          onClick={() => {
            if (!isGroupingMode) toggleGroupExpansion(content.id);
          }}
          sx={{
            cursor: isGroupingMode ? "default" : "pointer",
            backgroundColor: isDropActive
              ? alpha(theme.palette.primary.main, 0.1)
              : "transparent",
            "& > td:first-of-type": {
              borderLeft:
                isExpanded && !isGroupingMode
                  ? `3px solid ${colors.primary.main}`
                  : "3px solid transparent",
            },
            "&:hover": {
              backgroundColor: isDropActive
                ? alpha(theme.palette.primary.main, 0.13)
                : theme.palette.action.hover,
            },
            transition: "background-color 0.2s ease",
          }}
        >
          {isGroupingMode && (
            <TableCell
              sx={{
                width: 44,
                paddingLeft: { xs: "6px !important", md: "16px !important" },
                paddingRight: "0 !important",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {showDragColumn && (
            <TableCell
              sx={{
                width: 32,
                p: "0 !important",
                textAlign: "center",
                verticalAlign: "middle",
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isGroupingMode) toggleGroupExpansion(content.id);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  height: "100%",
                  minHeight: 28,
                }}
              >
                <ChevronDown
                  size={16}
                  style={{
                    transform: isExpanded
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>
            </TableCell>
          )}
          <TableCell
            sx={{
              paddingLeft: { xs: "2px !important", md: "8px !important" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: { xs: 250, md: 380 },
              }}
            >
              {!showDragColumn && (
                <ChevronDown
                  size={16}
                  style={{
                    transform: isExpanded
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              )}
              <Tooltip
                title={`${content.name || "Grupo"} (${groupPieces.length} contenidos)`}
                arrow
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: {
                      xs: "0.75rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  {content.name || "Grupo"}{" "}
                  <Badge variant="outline" size="sm" sx={{ ml: 1 }}>
                    {content.piece_count ?? groupPieces.length} contenidos
                  </Badge>
                </Typography>
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell>
            {renderPlatformIcons(content.generated_platforms)}
          </TableCell>
          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              <Badge variant="outline" color={groupStatusInfo.color}>
                {groupStatusInfo.label}
              </Badge>
              {isContentReadOnly(content) && <ReadOnlyBadge />}
            </Box>
          </TableCell>
          {showClientColumn && (
            <TableCell
              sx={{
                paddingLeft: { xs: "2px !important", md: "8px !important" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Tooltip title={content.client_name || "Sin cliente"} arrow>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      size="sm"
                      name={content.client_name || "Cliente"}
                      src={getAvatarImageUrl(content.client_logo) || undefined}
                    />
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: {
                          xs: "0.7rem",
                          md: "0.75rem",
                        },
                      }}
                    >
                      {content.client_name || "Sin cliente"}
                    </Typography>
                  </Box>
                </Tooltip>
                {isSuperAdmin && content.agency_name && (
                  <Tooltip title={content.agency_name} arrow>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <AgencyIcon
                        size={19}
                        color={theme.palette.text.secondary}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: {
                            xs: "0.7rem",
                            md: "0.75rem",
                          },
                        }}
                      >
                        {content.agency_name}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          )}
          <DateCell value={getGroupScheduledAt(content)} icon={Calendar} />
          <DateCell value={content.created_at} />
          <TableCell
            align="center"
            onClick={(e) => e.stopPropagation()}
          >
            {canManageContent && !isGroupingMode && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e, content);
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <MoreVertical size={16} />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
        {isExpanded &&
          groupPieces.map((piece) => (
            <PieceRow
              key={`group-${groupId}-piece-${piece.id}`}
              content={piece}
              rowOptions={{
                nested: true,
                keyPrefix: `group-${groupId}-piece`,
                disableMenu: true,
              }}
            />
          ))}
      </React.Fragment>
    );
  };

  const UngroupDropZoneRow = () => {
    const rowRef = useRef(null);

    useEffect(() => {
      const element = rowRef.current;
      if (!element || !showDragColumn) return undefined;

      return dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          if (isGroupingMode) return false;
          if (source.data?.entityType !== "content-piece") return false;
          const sourcePiece = flatPiecesById[String(source.data?.pieceId)];
          return Boolean(sourcePiece?.group_id) && sourcePiece?.status === "draft";
        },
        getData: () => ({ entityType: "content-ungroup-target" }),
        onDragEnter: () => {
          setIsOverUngroupDropZone(true);
        },
        onDrag: () => {
          setIsOverUngroupDropZone(true);
        },
        onDragLeave: () => {
          setIsOverUngroupDropZone(false);
        },
        onDrop: ({ source }) => {
          const sourcePiece = flatPiecesById[String(source.data?.pieceId)];
          clearContentDragState();
          if (!sourcePiece?.group_id || sourcePiece.status !== "draft") return;
          openRemoveFromGroupConfirmation({ piece: sourcePiece });
        },
      });
    }, [
      clearContentDragState,
      flatPiecesById,
      isGroupingMode,
      openRemoveFromGroupConfirmation,
      showDragColumn,
    ]);

    return (
      <TableRow
        ref={rowRef}
        sx={{
          backgroundColor: isOverUngroupDropZone
            ? alpha(theme.palette.warning.main, 0.12)
            : alpha(theme.palette.warning.main, 0.06),
          "& td": {
            borderBottom: `1px dashed ${theme.palette.warning.main}`,
          },
        }}
      >
        <TableCell colSpan={totalTableColumns}>
          <Box
            sx={{
              py: 1,
              px: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: theme.palette.warning.dark,
              fontWeight: 600,
            }}
          >
            <X size={16} />
            Suelta aquí para quitar este contenido de su grupo actual.
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const viewModeTabs = (
    <Box data-tutorial="btn-calendar">
      <UnderlineTabs
        tabs={[
          { value: "table", label: "Listado", count: contentStatusTabs[0].count },
          { value: "calendar", label: "Calendario" },
        ]}
        value={viewMode}
        onChange={(mode) => {
          if (mode !== viewMode) {
            setViewMode(mode);
            setPiecesRefresh((prev) => prev + 1);
          }
        }}
      />
    </Box>
  );

  // Without an AppLayout client, the table's filter button picks the client (like other pages).
  const clientFilterOptions = useMemo(
    () => clients.map((client) => ({ value: client.id, label: client.name })),
    [clients]
  );
  const canFilterByClient = !isAppLayoutClientScoped;
  const selectedClientFilterOption =
    selectedTab === "all"
      ? null
      : clientFilterOptions.find((option) => String(option.value) === String(selectedTab)) ?? null;

  const applyClientFilter = (nextTab) => {
    if (String(nextTab) !== String(selectedTab)) {
      setSelectedTab(nextTab);
      setPiecesRefresh((prev) => prev + 1);
    }
  };

  const handleOpenClientFilterModal = () => {
    setDraftClientFilters({ client: selectedClientFilterOption });
    setIsClientFilterModalOpen(true);
  };

  const handleApplyClientFilters = () => {
    applyClientFilter(draftClientFilters.client?.value ?? "all");
    setIsClientFilterModalOpen(false);
  };

  const clientFilterConfigs = [
    {
      key: "client",
      title: "Cliente",
      description: "Muestra solo los contenidos de este cliente.",
      icon: Users,
      options: clientFilterOptions,
      loading: isLoadingClients,
      placeholder: "Selecciona un cliente",
      loadingPlaceholder: "Cargando clientes...",
    },
  ];

  const clientFilterChips =
    canFilterByClient && selectedClientFilterOption
      ? [
        {
          key: "client",
          label: `Cliente: ${selectedClientFilterOption.label}`,
          onRemove: () => applyClientFilter("all"),
        },
      ]
      : [];

  // The AppLayout filter (agency for superadmin, client otherwise) shows as a chip too, like other tables.
  const appLayoutFilterChips =
    selectedFilterData?.id && selectedFilterData.id !== "global" && selectedFilterData.name
      ? [
        {
          key: "applayout-filter",
          label: `${isSuperAdmin ? "Agencia" : "Cliente"}: ${selectedFilterData.name}`,
          onRemove: () => changeSelectedFilter("global"),
        },
      ]
      : [];

  // Set page context
  React.useEffect(() => {
    const computedTitle = "Generador de contenidos";

    let breadcrumbItems = null;

    if (isSuperAdmin) {
      breadcrumbItems =
        selectedFilterData && selectedFilterData.id !== "global"
          ? [
            {
              label: "Vista global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Agencia: ${selectedFilterData.name}` },
          ]
          : [{ label: "Vista global" }];
    } else if (isAppLayoutClientScoped) {
      breadcrumbItems = [
        {
          label: "Vista global",
          onClick: () => changeSelectedFilter("global"),
        },
        { label: `Cliente: ${selectedClientFilterName || selectedClientName || "Cliente"}` },
      ];
    } else if (isAgencyScopedUser && (!selectedFilterData || selectedFilterData.id === "global")) {
      breadcrumbItems = [{ label: "Vista general" }];
    }

    const rightContent = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {isAgencyAdmin && (
          <AgencyPromptsButton steps={PROMPT_STEPS} variant="header" />
        )}
        {canManageContent && (
          <Button data-tutorial="btn-generate" onClick={handleOpenCreateContentModal}>
            <Plus size={16} />
            Crear contenido
          </Button>
        )}
      </Box>
    );

    setPageContext({
      title: computedTitle,
      showTitle: true,
      titleRightContent: tutorialsEnabled && canManageContent ? (
        <TutorialStartButton onClick={handleOpenContentTutorialIntro} />
      ) : null,
      rightContent,
      breadcrumbItems,
    });
  }, [
    canManageContent,
    setPageContext,
    navigate,
    isAppLayoutClientScoped,
    selectedClientName,
    selectedFilterData,
    isSuperAdmin,
    isAgencyAdmin,
    changeSelectedFilter,
    handleOpenContentTutorialIntro,
    handleOpenCreateContentModal,
    selectedClient,
  ]);

  const upcomingPanel = (
    <UpcomingPublications
      layout={upcomingLayout}
      filters={upcomingFilters}
      refreshKeys={[piecesRefresh, contentGeneratorRefreshKey, filterRefresh]}
      showClientName={showClientColumn}
      clientLogoByConfigId={clientLogoByConfigId}
      mockAdaptations={tutorialList?.upcoming}
      onToggleView={() => setViewMode(viewMode === "calendar" ? "table" : "calendar")}
      isCalendarView={viewMode === "calendar"}
      onItemClick={(item) =>
        handleContentPieceClick(
          { id: item.pieceId, group_id: item.groupId, config: item.configId },
          item.adaptationId
        )
      }
    />
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          height: "calc(100vh - 165px)",
          minHeight: isUpcomingOnTop ? 960 : 0,
          overflow: "hidden",
          flex: 1,
        }}
      >
        {/* Content */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          {viewModeTabs}
          {/* Content area - different layouts based on view */}
          {/* Upcoming publications sit next to (or above) both the table and the calendar */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: upcomingLayout === "side" ? "row" : "column",
              gap: 2,
            }}
          >
          {upcomingLayout === "top" && upcomingPanel}
          {viewMode === "calendar" ? (
            /* Calendar view - with Card wrapper */
            <Card
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: neutrals.white,
                p: 0,
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden",
                  padding: "16px",
                  "&:only-child": {
                    padding: "16px",
                  },
                }}
              >
                <ContentCalendar
                  clientId={selectedClient?.id}
                  platformAdaptations={calendarAdaptations}
                  showClientName={
                    showClientColumn
                  }
                  clientNameById={clientNameById}
                  clientNameByConfigId={clientNameByConfigId}
                  clientLogoByConfigId={clientLogoByConfigId}
                  onScheduleClick={handleOpenScheduleModal}
                  onContentClick={(piece, adaptationId) => {
                    // Force has_raw_content and has_platform_adaptations to true for calendar events
                    // since they always have platform adaptations (they're scheduled)
                    handleContentPieceClick(
                      { ...piece, has_raw_content: true, has_platform_adaptations: true },
                      adaptationId
                    );
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            /* Table view: toolbar + tabla + paginación unificados en DataTable */
            <Box
              data-tutorial="content-table"
              ref={(node) => {
                // El auto-scroll del drag & drop necesita el elemento que hace scroll (el TableContainer).
                scrollContainerRef.current = node?.querySelector(".MuiTableContainer-root") ?? null;
              }}
              sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, gap: 1.5 }}
            >
              {isBatchGenerating && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: "12px",
                    border: `1px solid ${neutrals.gray[200]}`,
                    backgroundColor: neutrals.gray[50],
                  }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Se está generando una tanda de contenidos. Irán apareciendo en la tabla.
                  </Typography>
                </Box>
              )}
              <DataTable
                columns={contentTableColumns}
                rows={contentPieces}
                getRowId={(content) =>
                  isGroupEntry(content) ? `group-${content.id}` : `piece-${content.id}`
                }
                renderRow={(content, index) => (
                  <>
                    {index === 0 && shouldShowUngroupDropZone && <UngroupDropZoneRow />}
                    {isGroupEntry(content) ? (
                      <GroupRow content={content} />
                    ) : (
                      <PieceRow content={content} />
                    )}
                  </>
                )}
                loading={showTableSkeleton}
                skeletonRows={Math.min(pageSize, 8)}
                emptyContent={
                  hasLoadedContentPieces && !isLoadingContentPieces ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <FileText size={48} color={neutrals.gray[400]} style={{ marginBottom: 12 }} />
                      <Typography sx={{ color: neutrals.gray[600], fontSize: "0.95rem" }}>
                        No se encontraron contenidos con los filtros actuales.
                      </Typography>
                    </Box>
                  ) : (
                    <></>
                  )
                }
                stickyHeader
                tableMinWidth={350}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  // La tabla ocupa el alto disponible y hace scroll (vertical y lateral) dentro de la card.
                  "& .MuiTableContainer-root": {
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                  },
                }}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                searchPlaceholder="Buscar contenidos..."
                tabs={contentStatusTabs}
                activeTab={selectedStatus}
                onTabChange={handleStatusChange}
                onFilterClick={canFilterByClient ? handleOpenClientFilterModal : undefined}
                activeFiltersCount={clientFilterChips.length}
                filterTooltip="Filtros"
                filterChips={[...appLayoutFilterChips, ...clientFilterChips]}
                paginationStart={
                  canManageContent && selectedConfigId ? (
                    isGroupingMode ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelGroupingMode}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          <X size={16} />
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleOpenCreateGroupModal}
                          disabled={selectedGroupPieceIds.length < 2}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          <Layers size={16} />
                          Agrupar ({selectedGroupPieceIds.length})
                        </Button>
                      </>
                    ) : (
                      <Button
                        data-tutorial="btn-group-content"
                        variant="outline"
                        size="sm"
                        onClick={handleStartGroupingMode}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        <Layers size={16} />
                        Agrupar contenidos
                      </Button>
                    )
                  ) : null
                }
                page={page}
                rowsPerPage={pageSize}
                totalCount={totalCount}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                hidePaginationWhenEmpty={false}
              />
            </Box>
          )}
          {upcomingLayout === "side" && upcomingPanel}
          </Box>
        </Box>
      </Box>

      {/* Context menu */}
      <DropdownMenuContent
        anchorEl={null}
        anchorReference="anchorPosition"
        anchorPosition={menuAnchorPosition}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {canEditContent && (
          <DropdownMenuItem onClick={handleEditContent}>
            <Edit size={16} />
            Editar
          </DropdownMenuItem>
        )}
        {canSendToClient && (
          <DropdownMenuItem onClick={handleSendToReview}>
            <Send size={16} />
            Enviar al cliente
          </DropdownMenuItem>
        )}
        {canApproveContent && (
          <DropdownMenuItem onClick={handleApproveContent}>
            <CheckCircle size={16} />
            Marcar como aceptado
          </DropdownMenuItem>
        )}
        {menuPersonasTarget && (
          <DropdownMenuItem onClick={handleEditBuyerPersonas}>
            <Users size={16} />
            Editar buyer persona de {menuPersonasTarget.clientName}
          </DropdownMenuItem>
        )}
        {canDeleteContent && (
          <DropdownMenuItem
            onClick={handleDeleteContent}
            sx={{ color: theme.palette.error.main }}
          >
            <Trash2 size={16} />
            Eliminar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>

      <IncidentFiltersModal
        open={isClientFilterModalOpen}
        onClose={() => setIsClientFilterModalOpen(false)}
        title="Filtros"
        filterConfigs={clientFilterConfigs}
        draftFilters={draftClientFilters}
        onChange={(field, value) => setDraftClientFilters((prev) => ({ ...prev, [field]: value }))}
        onApply={handleApplyClientFilters}
        onClearAll={() => setDraftClientFilters({ client: null })}
      />

      <CreateContentModal
        open={showCreateContentModal}
        onClose={handleCloseCreateContentModal}
        configuredClients={clients}
        defaultClientId={createContentDefaultClientId}
        agencyFilterId={filterParam?.key === "agency" ? filterParam.value : null}
        onContinue={handleCreateContentContinue}
      />

      {/* Tutorial: the create modal prefilled with the mock client (nothing is saved). */}
      <CreateContentModal
        open={Boolean(tutorialMock?.createModal)}
        onClose={() => {}}
        configuredClients={[]}
        onContinue={() => {}}
        tutorialMock={tutorialMock?.createModal ?? null}
      />

      <EditBuyerPersonasModal
        target={personasEditTarget}
        onClose={() => setPersonasEditTarget(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title={
          isMenuTargetGroup
            ? `Eliminar grupo: ${selectedContentForMenu?.name || "Sin nombre"}`
            : `Eliminar contenido: ${selectedContentForMenu?.title || "Sin título"}`
        }
        question={
          isMenuTargetGroup
            ? "¿Estás seguro de que quieres eliminar este grupo?"
            : "¿Estás seguro de que quieres eliminar este contenido?"
        }
        description={
          isMenuTargetGroup ? (
            <>
              Se eliminará el grupo{" "}
              <strong>"{selectedContentForMenu?.name || "Sin nombre"}"</strong>,
              pero <strong>los contenidos del grupo no se eliminarán</strong>.
              Seguirán disponibles de forma independiente en la lista de contenidos.
            </>
          ) : (
            <>
              Esta acción no se puede deshacer. Se eliminará permanentemente el
              contenido{" "}
              <strong>"{selectedContentForMenu?.title || "Sin título"}"</strong>.
              {selectedContentForMenu?.publication_status === "scheduled" && (
                <>
                  {" "}Está <strong>programado en redes sociales</strong>: se cancelará su
                  programación y no llegará a publicarse.
                </>
              )}
            </>
          )
        }
        confirmLabel={isMenuTargetGroup ? "Eliminar grupo" : "Eliminar contenido"}
        tone="danger"
      />

      {/* Send to Client Confirmation Modal */}
      <ContentConfirmationModal
        open={showSendToClientModal}
        onClose={() => setShowSendToClientModal(false)}
        onConfirm={handleConfirmSendToReview}
        action="send_to_client"
        contentData={{
          type: isGroupEntry(selectedContentForMenu) ? "group" : "content",
          name: isGroupEntry(selectedContentForMenu) ? selectedContentForMenu?.name : undefined,
          group_id: selectedContentForMenu?.group_id,
          group_name: selectedContentForMenu?.group_name,
          title: selectedContentForMenu?.title,
        }}
        isLoading={isSaving}
      />

      {/* Approve Content Confirmation Modal */}
      <ContentConfirmationModal
        open={showApproveContentModal}
        onClose={() => setShowApproveContentModal(false)}
        onConfirm={handleConfirmApproveContent}
        action="approve"
        contentData={{
          type: isGroupEntry(selectedContentForMenu) ? "group" : "content",
          name: isGroupEntry(selectedContentForMenu) ? selectedContentForMenu?.name : undefined,
          group_id: selectedContentForMenu?.group_id,
          group_name: selectedContentForMenu?.group_name,
          title: selectedContentForMenu?.title,
        }}
        isLoading={isSaving}
      />

      {/* Group Membership Confirmation Modal */}
      <ContentConfirmationModal
        open={Boolean(pendingGroupMembershipAction)}
        onClose={handleCloseGroupMembershipModal}
        onConfirm={handleConfirmGroupMembershipAction}
        action={pendingGroupMembershipAction?.action}
        contentData={{
          title: pendingGroupMembershipAction?.piece?.title,
          source_group_name: pendingGroupMembershipAction?.source_group_name,
          target_group_name: pendingGroupMembershipAction?.target_group_name,
        }}
        isLoading={isSaving}
      />

      {/* Create Group Modal */}
      <UnifiedModal
        open={showCreateGroupModal}
        onClose={() => {
          setShowCreateGroupModal(false);
          setNewGroupName("");
        }}
        title="Agrupar contenidos"
        maxWidth="xs"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Se agruparán <strong>{selectedGroupPieceIds.length} contenidos</strong> en un grupo. Asigna un nombre para identificarlo.
          </Typography>
          <Box>
            <Label sx={{ mb: 1, display: "block", fontWeight: 500 }}>
              Nombre del grupo <span style={{ color: theme.palette.error.main }}>*</span>
            </Label>
            <CustomTextField
              placeholder="Ej: Campaña enero, Pack redes sociales..."
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newGroupName.trim()) {
                  handleCreateGroup();
                }
              }}
              maxLength={100}
              autoFocus
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateGroupModal(false);
                setNewGroupName("");
              }}
              disabled={isCreatingGroup}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateGroup}
              disabled={!newGroupName.trim() || isCreatingGroup}
            >
              {isCreatingGroup ? "Agrupando..." : "Confirmar agrupación"}
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Schedule Content Modal */}
      <UnifiedModal
        open={showScheduleModal}
        onClose={handleCloseScheduleModal}
        title={
          contentToSchedule
            ? `Programar contenido: ${contentToSchedule?.title || "Sin título"}`
            : "Programar contenido"
        }
        maxWidth="sm"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {isScheduleSelectionMode && (
            <Box>
              <Label
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <FileText size={16} color={theme.palette.text.secondary} />
                Contenido
              </Label>
              <CustomSelector
                value={contentToSchedule ? String(contentToSchedule.id) : ""}
                onChange={handleScheduleContentSelect}
                options={scheduleContentOptions}
                placeholder="Selecciona un contenido..."
                searchable
                disabled={!scheduleContentOptions.length}
              />
            </Box>
          )}
          <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
            La fecha es la de la pieza: todas sus redes se publican a la vez. Para enviarla a las
            redes, ábrela y prográmala desde el paso Programación.
          </Typography>
          <Box>
            <Label
              sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Calendar size={16} color={theme.palette.text.secondary} />
              Fecha y hora de programación
            </Label>
            <DateTimePickerField
              value={scheduleDateTime}
              onChange={setScheduleDateTime}
              disabled={!contentToSchedule}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outline"
              onClick={handleCloseScheduleModal}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSchedule}
              disabled={
                !scheduleDateTime || !contentToSchedule
              }
            >
              Guardar fecha
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Success/Error Popup */}
      <SuccessPopup
        message={popupMessage}
        isOpen={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          setIsPopupError(false);
        }}
        autoHideDuration={3000}
        error={isPopupError}
      />

      <TutorialIntroModal
        open={tutorialsEnabled && showTutorialIntroModal}
        onClose={handleCloseContentTutorialIntro}
        onConfirm={handleConfirmContentTutorial}
        isFirstTimePrompt={isFirstTimeTutorialPrompt}
        {...CONTENT_GENERATOR_TUTORIAL_INTRO}
      />

    </>
  );
}
